/**
 * Locations Page
 * Manage and view edge locations in a table format with hierarchical sidebar
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { Location, Region, Country, AggregatedCapabilities, Cluster } from '@simcheck/shared-types';
import { resolveModemModelByReportedName } from '@simcheck/shared-types';
import { CapabilitiesCell } from '../components/locations/CapabilitiesCell';
import { MapPin, Plus, RefreshCw, Shield, AlertCircle, Globe, Flag, Edit, Trash2, Server, ChevronDown, Map as MapIcon, Signal, Layers } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Button, Spinner, Alert, Badge, ConfirmDialog, Card, Popover, PopoverTrigger, PopoverContent } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { MobileFilterSheet } from '@/components/layout/MobileFilterSheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { CreateRegionModal } from '../components/locations/CreateRegionModal';
import { CreateCountryModal } from '../components/locations/CreateCountryModal';
import { CreateLocationModal } from '../components/locations/CreateLocationModal';
import { EditLocationModal } from '../components/locations/EditLocationModal';
import { EditRegionModal } from '../components/locations/EditRegionModal';
import { EditCountryModal } from '../components/locations/EditCountryModal';
import { AssignModemModal } from '../components/locations/AssignModemModal';
import { LocationsGoogleMap } from '../components/locations/LocationsGoogleMap';
import { LocationHierarchySidebar } from '../components/locations/LocationHierarchySidebar';
import { CreateClusterModal } from '../components/clusters/CreateClusterModal';
import { EditClusterModal } from '../components/clusters/EditClusterModal';
import { ClusterDetailPanel } from '../components/clusters/ClusterDetailPanel';
import { getFullHierarchicalLocations, filterHierarchy, filterLocationsByTenancy, deleteLocation } from '../services/hierarchicalLocationService';
import { edgeDeviceService } from '../services/edgeDeviceService';
import { getAllModemModels } from '../services/modemModelService';
import { getClustersForOrg } from '../services/clusterService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDeviceVisibility, filterDevicesByTier } from '../hooks/useDeviceVisibility';

// Network info from modem scans
interface NetworkInfo {
  networkId: string;
  networkName: string;
  mcc: string;
  mnc: string;
}

// Define enriched location type (extends Location with display fields)
interface EnrichedLocation extends Omit<Location, 'availableNetworks'> {
  regionName: string;
  countryName: string;
  countryFlag: string;
  aggregatedCapabilities?: AggregatedCapabilities;
  availableNetworks?: NetworkInfo[];
}

// Filter column configuration helper
const filterColumnHelper = createColumnConfigHelper<EnrichedLocation>();

// Static filter configs (type and status have fixed options)
const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('displayName')
    .accessor((row) => row.displayName)
    .displayName('Location Name')
    .icon(MapPin)
    .build(),
  filterColumnHelper
    .text()
    .id('city')
    .accessor((row) => row.city || '')
    .displayName('City')
    .icon(MapPin)
    .build(),
  filterColumnHelper
    .option()
    .id('type')
    .accessor((row) => row.type)
    .displayName('Type')
    .icon(MapPin)
    .options([
      { label: 'Datacenter', value: 'datacenter' },
      { label: 'Office', value: 'office' },
      { label: 'Lab', value: 'lab' },
    ])
    .build(),
  filterColumnHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(MapPin)
    .options([
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ])
    .build(),
];

export const Locations: React.FC = () => {
  const { user, isSuperAdmin, hasSystemRole, activeOrganization, userRole } = useAuth();
  const visibilityFlags = useDeviceVisibility();
  const { success, error: showError } = useToast();
  const [regions, setRegions] = useState<Region[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  // Unfiltered hierarchy for the create/edit forms (sidebar + map use the filtered one)
  const [allRegions, setAllRegions] = useState<Region[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [locations, setLocations] = useState<EnrichedLocation[]>([]);
  // A cluster is a group of places you RUN AT, so its picker must span every
  // location the org can test at — SimCheck's network included — even though
  // the sites table above it lists only the org's own locations.
  const [clusterLocations, setClusterLocations] = useState<EnrichedLocation[]>([]);
  const [clusterRegions, setClusterRegions] = useState<Region[]>([]);
  const [clusterCountries, setClusterCountries] = useState<Country[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateRegion, setShowCreateRegion] = useState(false);
  const [showCreateCountry, setShowCreateCountry] = useState(false);
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [showEditRegion, setShowEditRegion] = useState(false);
  const [showEditCountry, setShowEditCountry] = useState(false);
  const [showAssignModem, setShowAssignModem] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateCluster, setShowCreateCluster] = useState(false);
  const [showEditCluster, setShowEditCluster] = useState(false);
  const [selectedLocationForAction, setSelectedLocationForAction] = useState<Location | null>(null);
  const [selectedRegionForEdit, setSelectedRegionForEdit] = useState<Region | null>(null);
  const [selectedCountryForEdit, setSelectedCountryForEdit] = useState<Country | null>(null);
  const [selectedClusterForEdit, setSelectedClusterForEdit] = useState<Cluster | null>(null);
  const [showMap, setShowMap] = useState(true);
  // How many locations the map actually plotted. A location with no stored
  // coordinates that can't be geocoded gets no marker, so captioning the map
  // with the raw location count would overstate what's on it.
  const [mappedCount, setMappedCount] = useState<number | null>(null);

  // Sidebar hierarchy selection state
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedCountryId, setSelectedCountryId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  
  // Determine if user can see sensitive location data
  const canViewSensitiveData = isSuperAdmin || hasSystemRole('super_admin') || hasSystemRole('support');

  // Tenant-owned locations (#202): org owner/admin can create private
  // locations and manage the ones their org owns.
  const canManageOrgLocations = isSuperAdmin || userRole === 'owner' || userRole === 'admin';
  const isOwnLocation = (loc: { ownerOrgId?: string | null }) =>
    !!activeOrganization?.id && loc.ownerOrgId === activeOrganization.id;
  // Single gate for every edit affordance on this page (table row, map info
  // window, hierarchy sidebar). It mirrors the server's rule in
  // PUT /locations/:id — shared locations (ownerOrgId null) belong to the
  // platform, so a tenant can see them but never edit them.
  const canEditLocation = useCallback(
    (loc?: { ownerOrgId?: string | null } | null) =>
      !!loc && (isSuperAdmin || (canManageOrgLocations && isOwnLocation(loc))),
    [isSuperAdmin, canManageOrgLocations, activeOrganization?.id],
  );

  useEffect(() => {
    loadLocations();
  }, [activeOrganization?.id]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch locations, edge devices, modem models, and clusters in parallel
      const [data, devices, modemModels, clustersData] = await Promise.all([
        getFullHierarchicalLocations(),
        edgeDeviceService.getDevices().catch((err) => {
          console.error('[Locations] Failed to fetch devices:', err);
          return [];
        }),
        getAllModemModels(true).catch((err) => {
          console.error('[Locations] Failed to fetch modem models:', err);
          return [];
        }),
        activeOrganization?.id
          ? getClustersForOrg(activeOrganization.id).catch((err) => {
              console.error('[Locations] Failed to fetch clusters:', err);
              return [];
            })
          : Promise.resolve([])
      ]);

      // Build a map of modemModelId -> capabilities for inheritance lookup
      const modemModelCapabilities = new Map<string, Record<string, any>>();
      for (const model of modemModels) {
        if (model.id && model.capabilities) {
          modemModelCapabilities.set(model.id, model.capabilities as Record<string, any>);
        }
      }

      // Filter devices by tier based on org access
      const visibleDevices = filterDevicesByTier(devices, visibilityFlags);

      // Aggregate data per location from visible edge devices
      const locationData = new Map<string, {
        totalModems: number;
        availableModems: number;
        networks: Map<string, { networkId: string; networkName: string; mcc: string; mnc: string }>;
        technologies: Set<string>;
        features: Set<string>;
      }>();

      for (const device of visibleDevices) {
        if (!device.locationId) continue;

        // Initialize location data if not exists
        if (!locationData.has(device.locationId)) {
          locationData.set(device.locationId, {
            totalModems: 0,
            availableModems: 0,
            networks: new Map(),
            technologies: new Set(),
            features: new Set()
          });
        }

        const locData = locationData.get(device.locationId)!;

        // Count modems
        // A modem is available if the parent device is online AND the modem is not explicitly disconnected/error
        const deviceModems = device.modems || [];
        locData.totalModems += deviceModems.length;
        const deviceIsOnline = device.status === 'online';
        locData.availableModems += deviceModems.filter(m => {
          // If device is offline, no modems are available
          if (!deviceIsOnline) return false;
          // If device is online, modem is available unless explicitly disconnected or error
          const modemStatus = m.status;
          return modemStatus !== 'disconnected' && modemStatus !== 'error';
        }).length;

        // Aggregate networks and capabilities from each modem
        for (const modem of deviceModems) {
          // Available networks from AT+COPS scan
          if (modem.availableNetworks && Array.isArray(modem.availableNetworks)) {
            for (const network of modem.availableNetworks) {
              const key = `${network.mcc || ''}-${network.mnc || ''}-${network.operator_name || ''}`;
              if (!locData.networks.has(key)) {
                locData.networks.set(key, {
                  networkId: key,
                  networkName: network.operator_name || 'Unknown',
                  mcc: network.mcc || '',
                  mnc: network.mnc || ''
                });
              }
            }
          }

          // Capability resolution, in order of authority: per-modem overrides,
          // the linked catalog model (`modemModelId`), then the model STRING the
          // edge reported. The last exists because this reads the legacy
          // `devices.modems[]` array, which carries `model` but neither
          // capabilities nor a model link — the real link lives on
          // `devices/{id}/known_modems/{imei}` and never reaches this payload,
          // which is why every location read "No data".
          let caps = modem.capabilities as Record<string, any> | undefined;
          if (!caps || Object.keys(caps).length === 0) {
            const linked = modem.modemModelId
              ? modemModelCapabilities.get(modem.modemModelId)
              : undefined;
            if (linked) {
              caps = linked;
            } else {
              const matched = resolveModemModelByReportedName(
                (modem as any).model,
                modemModels as any[],
              );
              if (matched?.capabilities) caps = matched.capabilities as Record<string, any>;
            }
          }

          if (caps && Object.keys(caps).length > 0) {
            // Helper to get capability value (dynamic format: { type, value })
            const getCapValue = (key: string): boolean => {
              const cap = caps![key];
              if (!cap) return false;
              return typeof cap === 'object' && 'value' in cap ? !!cap.value : !!cap;
            };

            // Technologies
            if (getCapValue('supports5G')) locData.technologies.add('5G');
            if (getCapValue('supports4G')) locData.technologies.add('4G');
            if (getCapValue('supports3G')) locData.technologies.add('3G');
            if (getCapValue('supports2G')) locData.technologies.add('2G');
            if (getCapValue('supportsLTEM')) locData.technologies.add('LTE-M');
            if (getCapValue('supportsNBIoT')) locData.technologies.add('NB-IoT');

            // Features
            if (getCapValue('supportsVoLTE')) locData.features.add('VoLTE');
            if (getCapValue('supportseSIM')) locData.features.add('eSIM');
            if (getCapValue('supportsVoNR')) locData.features.add('VoNR');
            if (getCapValue('hasGPS')) locData.features.add('GPS');
            if (getCapValue('supportsCarrierAggregation')) locData.features.add('CA');
          }
        }
      }

      // Sort technologies for consistent display
      const techOrder: Record<string, number> = { '5G': 1, '4G': 2, 'LTE-M': 3, 'NB-IoT': 4, '3G': 5, '2G': 6 };

      // Enrich locations with aggregated data from devices + region/country names
      const enrichedLocationsWithData: EnrichedLocation[] = data.locations.map(location => {
        const locData = locationData.get(location.id);
        const sortedTech = locData
          ? Array.from(locData.technologies).sort((a, b) => (techOrder[a] || 99) - (techOrder[b] || 99))
          : [];
        const region = data.regions.find(r => r.id === location.regionId);
        const country = data.countries.find(c => c.id === location.countryId);

        return {
          ...location,
          regionName: region?.name || 'Unknown',
          countryName: country?.name || 'Unknown',
          countryFlag: country?.flag || '',
          modemCount: locData?.totalModems ?? 0,  // Only use calculated values from devices, not stale Firestore values
          availableModemCount: locData?.availableModems ?? 0,
          availableNetworks: locData ? Array.from(locData.networks.values()) : [],
          aggregatedCapabilities: locData ? {
            technologies: sortedTech,
            features: Array.from(locData.features),
            modemCount: locData.totalModems,
            lastUpdatedAt: new Date().toISOString()
          } : undefined
        } as EnrichedLocation;
      });

      // Tenancy (#202) via the shared helper: other orgs' private locations
      // are never shown, and synthetic demo locations stay scoped to their
      // owning org even for super admins.
      const tenancyVisible = filterLocationsByTenancy(
        enrichedLocationsWithData,
        activeOrganization?.id,
        isSuperAdmin,
        { hasDeviceAt: (id) => locationData.has(id) },
      );
      // Scope (#270): this page is "my sites and my clusters" for a tenant —
      // ONLY locations their org owns. SimCheck's own estate is capacity, and
      // belongs on the Edges page where it is labelled as such; listing it here
      // is what made customers think shared modems were theirs to manage.
      // Empty own sites stay visible — a fresh default location must be
      // manageable before any device lands on it. Super admins see everything.
      const visibleLocations = isSuperAdmin
        ? tenancyVisible
        : tenancyVisible.filter(
            loc => (loc as any).ownerOrgId === activeOrganization?.id,
          );

      // Everywhere the org can actually run a test: SimCheck's sites that have
      // hardware, plus its own. Feeds the cluster pickers only.
      const testableLocations = isSuperAdmin
        ? tenancyVisible
        : tenancyVisible.filter(
            loc => locationData.has(loc.id) || (loc as any).ownerOrgId === activeOrganization?.id,
          );

      // Filter hierarchy to only show regions/countries with visible locations
      const filtered = filterHierarchy(data.regions, data.countries, visibleLocations as Location[]);
      setRegions(filtered.regions);
      setCountries(filtered.countries);
      // Create/edit forms get the UNFILTERED hierarchy — otherwise a region or
      // country with no location yet can never be picked for a new location.
      setAllRegions(data.regions);
      setAllCountries(data.countries);
      setLocations(visibleLocations);
      const clusterHierarchy = filterHierarchy(
        data.regions,
        data.countries,
        testableLocations as Location[],
      );
      setClusterRegions(clusterHierarchy.regions);
      setClusterCountries(clusterHierarchy.countries);
      setClusterLocations(testableLocations);
      setClusters(clustersData);
    } catch (error) {
      console.error('Failed to load locations:', error);
      setError(error instanceof Error ? error.message : 'Failed to load locations');
      setLocations([]);
      setClusterLocations([]);
      setClusterRegions([]);
      setClusterCountries([]);
      setRegions([]);
      setCountries([]);
      setAllRegions([]);
      setAllCountries([]);
      setClusters([]);
    } finally {
      setLoading(false);
    }
  };

  // Locations are already enriched in loadLocations, just use them directly
  const enrichedLocations = locations;

  // Build dynamic filter config with region/country options from state
  const filterColumnsConfig = useMemo(() => {
    // Get unique region names from enriched locations
    const uniqueRegions = [...new Set(enrichedLocations.map(l => l.regionName).filter(r => r !== 'Unknown'))].sort();
    // Get unique country names with flags
    const uniqueCountries = [...new Set(enrichedLocations.map(l => l.countryName).filter(c => c !== 'Unknown'))].sort();

    return [
      ...getStaticFilterConfigs(),
      filterColumnHelper
        .option()
        .id('regionName')
        .accessor((row) => row.regionName)  // Use display name, not ID
        .displayName('Region')
        .icon(Globe)
        .options(uniqueRegions.map(r => ({ label: r, value: r })))
        .build(),
      filterColumnHelper
        .option()
        .id('countryName')
        .accessor((row) => row.countryName)  // Use display name, not ID
        .displayName('Country')
        .icon(Flag)
        .options(uniqueCountries.map(c => ({ label: c, value: c })))
        .build(),
    ];
  }, [enrichedLocations]);

  // Initialize data table filters
  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: enrichedLocations,
    columnsConfig: filterColumnsConfig,
  });

  // Action handlers
  const handleAssignModem = (location: EnrichedLocation) => {
    setSelectedLocationForAction(location as unknown as Location);
    setShowAssignModem(true);
  };

  const handleEditLocation = (location: EnrichedLocation) => {
    setSelectedLocationForAction(location as unknown as Location);
    setShowEditLocation(true);
  };

  const handleDeleteLocation = (location: EnrichedLocation) => {
    setSelectedLocationForAction(location as unknown as Location);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = useCallback(async () => {
    if (!selectedLocationForAction) {
      showError('No location selected', 'Error');
      return;
    }

    if (!selectedLocationForAction.id) {
      showError('Invalid location data', 'Error');
      return;
    }

    try {
      await deleteLocation(selectedLocationForAction.id);
      success('Location deleted successfully', 'Success');
      await loadLocations();
      setShowDeleteConfirm(false);
      setSelectedLocationForAction(null);
    } catch (err) {
      console.error('Failed to delete location:', err);
      showError(err instanceof Error ? err.message : 'Failed to delete location', 'Error');
    }
  }, [selectedLocationForAction, success, showError]);

  const handleLocationUpdated = async () => {
    success('Location updated successfully', 'Success');
    await loadLocations();
  };

  const handleModemAssigned = async () => {
    success('Modem assigned successfully', 'Success');
    await loadLocations();
  };

  // Handlers for editing regions and countries from sidebar
  const handleEditRegion = (region: Region) => {
    setSelectedRegionForEdit(region);
    setShowEditRegion(true);
  };

  const handleEditCountry = (country: Country) => {
    setSelectedCountryForEdit(country);
    setShowEditCountry(true);
  };

  const handleEditLocationFromSidebar = (location: Location) => {
    setSelectedLocationForAction(location);
    setShowEditLocation(true);
  };

  const handleRegionUpdated = async () => {
    success('Region updated successfully', 'Success');
    await loadLocations();
  };

  const handleCountryUpdated = async () => {
    success('Country updated successfully', 'Success');
    await loadLocations();
  };

  // Handlers for clusters
  const handleEditCluster = (cluster: Cluster) => {
    setSelectedClusterForEdit(cluster);
    setShowEditCluster(true);
  };

  const handleSelectCluster = (clusterId: string | null) => {
    setSelectedClusterId(clusterId);
    // Clear location hierarchy selection when selecting a cluster
    if (clusterId) {
      setSelectedRegionId(null);
      setSelectedCountryId(null);
      setSelectedLocationId(null);
    }
  };

  const handleClusterUpdated = async () => {
    success('Cluster updated successfully', 'Success');
    await loadLocations();
  };

  const handleClusterCreated = async () => {
    success('Cluster created successfully', 'Success');
    await loadLocations();
  };

  // Apply filters + sidebar hierarchy selection
  const filteredLocations = useMemo(() => {
    // First apply sidebar hierarchy filter
    let hierarchyFiltered = enrichedLocations;

    if (selectedLocationId) {
      // Single location selected
      hierarchyFiltered = enrichedLocations.filter(l => l.id === selectedLocationId);
    } else if (selectedCountryId) {
      // Filter by country
      hierarchyFiltered = enrichedLocations.filter(l => l.countryId === selectedCountryId);
    } else if (selectedRegionId) {
      // Filter by region
      hierarchyFiltered = enrichedLocations.filter(l => l.regionId === selectedRegionId);
    }

    // Then apply DataTable filters
    return applyFilters(hierarchyFiltered, filters, filterColumnsConfig);
  }, [enrichedLocations, filters, filterColumnsConfig, selectedRegionId, selectedCountryId, selectedLocationId]);

  // Table columns
  const columns = [
    {
      key: 'displayName',
      header: 'Location',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-primary-500" />
          <div>
            <div className="font-medium text-foreground">
              {location?.displayName || '-'}
            </div>
            <div className="text-xs text-muted-foreground/70">
              {location?.name || '-'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'regionName',
      header: 'Region',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-accent-blue" />
          <span className="text-sm text-muted-foreground">
            {location?.regionName || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'countryName',
      header: 'Country',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <div className="flex items-center space-x-2">
          <Flag className="w-4 h-4 text-accent-purple" />
          <span className="text-sm text-muted-foreground">
            {location?.countryFlag || ''} {location?.countryName || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'city',
      header: 'City',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <span className="text-sm text-muted-foreground">
          {location?.city || '-'}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <Badge variant={location?.type === 'datacenter' ? 'default' : 'secondary'}>
          {location?.type || '-'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_value: any, location: EnrichedLocation) => (
        <Badge 
          variant={location?.status === 'active' ? 'success' : 'error'}
        >
          {location?.status || 'unknown'}
        </Badge>
      ),
    },
    {
      key: 'modems',
      header: 'Modems',
      render: (_value: any, location: EnrichedLocation) => (
        <div className="text-sm">
          <span className="font-medium text-foreground">
            {location?.availableModemCount || 0}
          </span>
          <span className="text-muted-foreground/70">
            {' / '}{location?.modemCount || 0}
          </span>
          <span className="text-xs text-muted-foreground/70 ml-1">
            available
          </span>
        </div>
      ),
    },
    {
      key: 'networks',
      header: 'Networks',
      render: (_value: any, location: EnrichedLocation) => {
        const availableNetworks = (location as any).availableNetworks as NetworkInfo[] | undefined;
        if (!availableNetworks || availableNetworks.length === 0) {
          return (
            <span className="text-muted-foreground/70 text-sm">None detected</span>
          );
        }
        const sorted = [...availableNetworks].sort((a, b) =>
          a.networkName.localeCompare(b.networkName)
        );
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                title="Show all detected networks"
                className="flex items-center gap-1 -mx-1 px-1 py-0.5 rounded-md hover:bg-muted transition-colors"
              >
                <Signal className="w-3.5 h-3.5 text-status-success" />
                <span className="text-sm font-medium">{availableNetworks.length}</span>
                <span className="text-xs text-muted-foreground/70 truncate max-w-[100px]">
                  ({sorted.slice(0, 2).map(n => n.networkName).join(', ')}{sorted.length > 2 ? '…' : ''})
                </span>
                <ChevronDown className="w-3 h-3 text-muted-foreground/70 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-72 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 border-b border-border">
                <p className="text-sm font-medium text-foreground">Detected networks</p>
                <p className="text-xs text-muted-foreground">
                  {availableNetworks.length} network{availableNetworks.length === 1 ? '' : 's'} from the latest modem scans
                </p>
              </div>
              <ul className="max-h-64 overflow-y-auto py-1">
                {sorted.map((n) => (
                  <li
                    key={n.networkId}
                    className="flex items-center justify-between gap-2 px-3 py-1.5"
                  >
                    <span className="text-sm text-foreground truncate">{n.networkName}</span>
                    {(n.mcc || n.mnc) && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0 font-mono">
                        {n.mcc} {n.mnc}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        );
      },
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      render: (_value: any, location: EnrichedLocation) => (
        <CapabilitiesCell capabilities={location.aggregatedCapabilities} />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_value: any, location: EnrichedLocation) => {
        const canEditThis = canEditLocation(location as any);
        // The org default location is the bootstrap landing spot for new
        // devices — tenants can rename it but never delete it (#202).
        const canDeleteThis =
          isSuperAdmin || (canEditThis && !(location as any).isDefault);
        return (
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignModem(location);
                }}
                variant="outline"
                size="sm"
                title="Manage Edges"
              >
                <Server className="w-4 h-4" />
              </Button>
            )}
            {canEditThis && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditLocation(location);
                }}
                variant="ghost"
                size="sm"
                title="Edit Location"
              >
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {canDeleteThis && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLocation(location);
                }}
                variant="ghost"
                size="sm"
                title="Delete Location"
              >
                <Trash2 className="w-4 h-4 text-status-error" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <AppShell>
      <div className="flex h-full">
        {/* Sidebar Panel — inline on desktop, overlay sheet on mobile */}
        {!isMobile && showSidebar && (
          <div className="w-64 flex-shrink-0 border-r border-border">
            <LocationHierarchySidebar
              regions={regions}
              countries={countries}
              locations={locations as unknown as Location[]}
              clusters={clusters}
              selectedRegionId={selectedRegionId}
              selectedCountryId={selectedCountryId}
              selectedLocationId={selectedLocationId}
              selectedClusterId={selectedClusterId}
              onSelectRegion={setSelectedRegionId}
              onSelectCountry={setSelectedCountryId}
              onSelectLocation={setSelectedLocationId}
              onSelectCluster={handleSelectCluster}
              onEditRegion={handleEditRegion}
              onEditCountry={handleEditCountry}
              onEditLocation={handleEditLocationFromSidebar}
              canEditLocation={canEditLocation}
              onEditCluster={handleEditCluster}
              onCreateRegion={() => setShowCreateRegion(true)}
              onCreateCountry={() => setShowCreateCountry(true)}
              onCreateCluster={() => setShowCreateCluster(true)}
              onRefresh={loadLocations}
              isSuperAdmin={isSuperAdmin}
              isCollapsed={!showSidebar}
              onToggleCollapse={() => setShowSidebar(false)}
            />
          </div>
        )}
        {isMobile && (
          <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
            <SheetContent side="left" className="w-72 p-0">
              <LocationHierarchySidebar
                regions={regions}
                countries={countries}
                locations={locations as unknown as Location[]}
                clusters={clusters}
                selectedRegionId={selectedRegionId}
                selectedCountryId={selectedCountryId}
                selectedLocationId={selectedLocationId}
                selectedClusterId={selectedClusterId}
                onSelectRegion={(id) => { setSelectedRegionId(id); setShowSidebar(false); }}
                onSelectCountry={(id) => { setSelectedCountryId(id); setShowSidebar(false); }}
                onSelectLocation={(id) => { setSelectedLocationId(id); setShowSidebar(false); }}
                onSelectCluster={(c) => { handleSelectCluster(c); setShowSidebar(false); }}
                onEditRegion={handleEditRegion}
                onEditCountry={handleEditCountry}
                onEditLocation={handleEditLocationFromSidebar}
                canEditLocation={canEditLocation}
                onEditCluster={handleEditCluster}
                onCreateRegion={() => setShowCreateRegion(true)}
                onCreateCountry={() => setShowCreateCountry(true)}
                onCreateCluster={() => setShowCreateCluster(true)}
                onRefresh={loadLocations}
                isSuperAdmin={isSuperAdmin}
                isCollapsed={false}
                onToggleCollapse={() => setShowSidebar(false)}
              />
            </SheetContent>
          </Sheet>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="p-2"
                >
                  {showSidebar ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
                </Button>
                <div className="min-w-0">
                  <h1 className="text-display font-semibold text-foreground mb-1">
                    Locations
                  </h1>
                  <p className="text-body text-muted-foreground">
                    Manage edge locations and view available modems
                    {(selectedRegionId || selectedCountryId || selectedLocationId) && (
                      <span className="ml-2 text-sm text-primary">
                        ({filteredLocations.length} filtered)
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={loadLocations}
                  disabled={loading}
                  variant="ghost"
                  size="sm"
                  icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
                >
                  <span className="hidden sm:inline">Refresh</span>
                </Button>

                {canManageOrgLocations && (
                  <Button
                    onClick={() => setShowCreateLocation(true)}
                    variant="default"
                    icon={<Plus className="w-4 h-4" />}
                  >
                    Location
                  </Button>
                )}
              </div>
            </div>

            {/* Security Status Banner */}
            {canViewSensitiveData && (
              <Alert variant="destructive">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      ADMIN VIEW: Showing full location details including GPS coordinates and addresses
                    </span>
                  </div>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-status-error-bg text-status-error">
                    SENSITIVE DATA
                  </span>
                </div>
              </Alert>
            )}

            {/* Location Map */}
            <Card className="overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setShowMap(!showMap)}
              >
                <div className="flex items-center gap-3">
                  <MapIcon className="w-5 h-5 text-primary" />
                  <h2 className="font-semibold text-foreground">Location Map</h2>
                  <Badge variant="outline" className="text-xs">
                    {mappedCount !== null && mappedCount < locations.length
                      ? `${mappedCount} of ${locations.length} mapped`
                      : `${locations.length} locations`}
                  </Badge>
                  {mappedCount !== null && mappedCount < locations.length && (
                    <span className="text-xs text-muted-foreground">
                      {locations.length - mappedCount} without coordinates
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${
                    showMap ? 'rotate-180' : ''
                  }`}
                />
              </div>

              {showMap && !loading && (
                <LocationsGoogleMap
                  locations={locations as unknown as Location[]}
                  countries={countries}
                  canViewSensitiveData={canViewSensitiveData}
                  onLocationSelect={(id) => {
                    // "View Details" opens the edit form only for locations the
                    // caller may edit; everyone else gets the read-only view.
                    const location = locations.find((l) => l.id === id);
                    if (location) handleEditLocation(location);
                  }}
                  selectedLocationId={selectedLocationForAction?.id}
                  height={isMobile ? '280px' : '400px'}
                  onMappedCountChange={setMappedCount}
                />
              )}
            </Card>

            {/* Error State */}
            {error && (
              <Alert variant="destructive">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              </Alert>
            )}

            {/* Filters */}
            <MobileFilterSheet activeCount={filters.length}>
              <Card className="p-4">
                <DataTableFilter
                  columns={filterColumns}
                  filters={filters}
                  actions={actions}
                  strategy={strategy}
                />
              </Card>
            </MobileFilterSheet>

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <Spinner size="lg" className="mx-auto mb-4" />
                  <p className="text-lg font-semibold text-foreground">
                    Loading locations...
                  </p>
                </div>
              </div>
            ) : (
              /* Data Table */
              <ResponsiveTable
                data={filteredLocations}
                columns={columns}
                keyExtractor={(location) => location?.id || `location-${Math.random()}`}
                searchable
                searchPlaceholder="Search locations by name, city, region, or country..."
                paginated
                defaultPageSize={10}
                emptyMessage="No locations found. Create your first location to get started."
                onRowClick={(location) => {
                  // TODO: Open location details modal or navigate to location details page
                }}
              />
            )}

            {/* Create Modals */}
            <CreateRegionModal
              isOpen={showCreateRegion}
              onClose={() => setShowCreateRegion(false)}
              onSuccess={loadLocations}
              userId={user?.uid || 'system'}
            />

            <CreateCountryModal
              isOpen={showCreateCountry}
              onClose={() => setShowCreateCountry(false)}
              onSuccess={loadLocations}
              regions={allRegions}
              userId={user?.uid || 'system'}
            />

            <CreateLocationModal
              isOpen={showCreateLocation}
              onClose={() => setShowCreateLocation(false)}
              onSuccess={loadLocations}
              regions={allRegions}
              countries={allCountries}
              userId={user?.uid || 'system'}
              ownerOrgId={isSuperAdmin ? undefined : activeOrganization?.id}
            />

            {/* Edit Location Modal */}
            {selectedLocationForAction && (
              <EditLocationModal
                isOpen={showEditLocation}
                onClose={() => {
                  setShowEditLocation(false);
                  setSelectedLocationForAction(null);
                }}
                onSuccess={handleLocationUpdated}
                location={selectedLocationForAction}
                readOnly={!canEditLocation(selectedLocationForAction)}
                canViewSensitiveData={canViewSensitiveData}
              />
            )}

            {/* Manage Edges Modal */}
            {selectedLocationForAction && (
              <AssignModemModal
                isOpen={showAssignModem}
                onClose={() => {
                  setShowAssignModem(false);
                  setSelectedLocationForAction(null);
                }}
                onSuccess={handleModemAssigned}
                location={selectedLocationForAction}
              />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
              open={showDeleteConfirm}
              onClose={() => {
                setShowDeleteConfirm(false);
                setSelectedLocationForAction(null);
              }}
              onConfirm={confirmDelete}
              title="Delete Location"
              message={`Are you sure you want to delete "${selectedLocationForAction?.displayName || selectedLocationForAction?.name}"? This action cannot be undone.`}
              confirmText="Delete"
              cancelText="Cancel"
              variant="destructive"
            />
          </div>
        </div>
      </div>

      {/* Edit Region Modal */}
      <EditRegionModal
        isOpen={showEditRegion}
        onClose={() => {
          setShowEditRegion(false);
          setSelectedRegionForEdit(null);
        }}
        onSuccess={handleRegionUpdated}
        region={selectedRegionForEdit}
      />

      {/* Edit Country Modal */}
      <EditCountryModal
        isOpen={showEditCountry}
        onClose={() => {
          setShowEditCountry(false);
          setSelectedCountryForEdit(null);
        }}
        onSuccess={handleCountryUpdated}
        country={selectedCountryForEdit}
        regions={allRegions}
      />

      {/* Create Cluster Modal */}
      <CreateClusterModal
        isOpen={showCreateCluster}
        onClose={() => setShowCreateCluster(false)}
        onSuccess={handleClusterCreated}
        regions={clusterRegions}
        countries={clusterCountries}
        locations={clusterLocations as unknown as Location[]}
        organizationId={activeOrganization?.id || ''}
        userId={user?.uid || 'system'}
      />

      {/* Edit Cluster Modal */}
      {selectedClusterForEdit && (
        <EditClusterModal
          isOpen={showEditCluster}
          onClose={() => {
            setShowEditCluster(false);
            setSelectedClusterForEdit(null);
          }}
          onSuccess={handleClusterUpdated}
          cluster={selectedClusterForEdit}
          regions={clusterRegions}
          countries={clusterCountries}
          locations={clusterLocations as unknown as Location[]}
        />
      )}

      {/* Cluster Detail Panel (shown when a cluster is selected) */}
      {selectedClusterId && (
        <ClusterDetailPanel
          clusterId={selectedClusterId}
          clusters={clusters}
          regions={clusterRegions}
          countries={clusterCountries}
          locations={clusterLocations as unknown as Location[]}
          onEdit={handleEditCluster}
          onClose={() => setSelectedClusterId(null)}
        />
      )}
    </AppShell>
  );
};
